<template>
  <div class="deletedResources">
    <b-tabs content-class="mt-3" lazy>
      <b-tab title="Courses" active>
        <b-overlay :show="fetchData" rounded="sm">
          <p v-if="!fetchData && !courses.length" class="text-center text-muted my-4">
            No deleted courses.
          </p>
          <table v-else class="table table-bordered table-striped">
            <thead class="thead-dark">
              <tr>
                <th>Si.No</th>
                <th class="align-middle">Course</th>
                <th class="align-middle">Code</th>
                <th class="align-middle">Class</th>
                <th class="align-middle">Programme</th>
                <th class="align-middle">Deleted On</th>
                <th class="align-middle">Deleted By</th>
                <th class="align-middle"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(course, index) in courses" :key="'course-' + course.id">
                <td>{{ index + 1 }}</td>
                <td class="align-middle text-capitalize">{{ course.name }}</td>
                <td class="align-middle">{{ course.code }}</td>
                <td class="align-middle">{{ course.class }}</td>
                <td class="align-middle">{{ course.programme }}</td>
                <td class="align-middle">{{ formatDate(course.deleted_at) }}</td>
                <td class="align-middle text-capitalize">{{ course.deleted_by }}</td>
                <td class="align-middle">
                  <b-button
                    size="sm"
                    variant="success"
                    :disabled="restoring === 'course-' + course.id"
                    @click="restore('course', course)"
                  >
                    <span v-if="restoring === 'course-' + course.id">Restoring...</span>
                    <span v-else>Restore</span>
                  </b-button>
                </td>
              </tr>
            </tbody>
          </table>
        </b-overlay>
      </b-tab>

      <b-tab :title="className">
        <b-overlay :show="fetchData" rounded="sm">
          <p v-if="!fetchData && !classes.length" class="text-center text-muted my-4">
            No deleted {{ className.toLowerCase() }}.
          </p>
          <table v-else class="table table-bordered table-striped">
            <thead class="thead-dark">
              <tr>
                <th>Si.No</th>
                <th class="align-middle">{{ className }}</th>
                <th class="align-middle">Code</th>
                <th class="align-middle">Programme</th>
                <th class="align-middle">Courses</th>
                <th class="align-middle">Deleted On</th>
                <th class="align-middle">Deleted By</th>
                <th class="align-middle"></th>
              </tr>
            </thead>
            <tbody v-for="(subgroup, index) in classes" :key="'class-' + subgroup.id">
              <tr>
                <td>{{ index + 1 }}</td>
                <td class="align-middle text-capitalize">{{ subgroup.name }}</td>
                <td class="align-middle">{{ subgroup.code }}</td>
                <td class="align-middle">{{ subgroup.programme }}</td>
                <td class="align-middle">
                  <b-link
                    v-if="deletedCourseCount(subgroup)"
                    class="text-info"
                    @click="toggleCourses(subgroup.id)"
                  >
                    {{ deletedCourseCount(subgroup) }}
                    {{ deletedCourseCount(subgroup) === 1 ? "course" : "courses" }}
                    <i :class="expanded.includes(subgroup.id) ? 'fas fa-caret-up' : 'fas fa-caret-down'"></i>
                  </b-link>
                  <span v-else class="text-muted">&mdash;</span>
                </td>
                <td class="align-middle">{{ formatDate(subgroup.deleted_at) }}</td>
                <td class="align-middle text-capitalize">{{ subgroup.deleted_by }}</td>
                <td class="align-middle">
                  <b-button
                    size="sm"
                    variant="success"
                    :disabled="restoring === 'class-' + subgroup.id"
                    @click="restore('class', subgroup)"
                  >
                    <span v-if="restoring === 'class-' + subgroup.id">Restoring...</span>
                    <span v-else>Restore</span>
                  </b-button>
                </td>
              </tr>
              <tr v-if="expanded.includes(subgroup.id)">
                <td colspan="8" class="text-left bg-light">
                  <div v-if="subgroup.courses_count">
                    <small class="text-muted">
                      Restored along with this {{ className.toLowerCase() }}:
                    </small>
                    <ul class="mb-0 mt-1">
                      <li v-for="course in subgroup.courses" :key="'c-' + course.id">
                        <span class="text-capitalize">{{ course.name }}</span>
                        <span class="text-muted"> ({{ course.code }})</span>
                      </li>
                    </ul>
                  </div>
                  <!-- deleted on their own before this class went, so the class restore leaves them
                       deleted - they are restored from the Courses tab afterwards -->
                  <div
                    v-if="subgroup.other_courses && subgroup.other_courses.length"
                    :class="{ 'mt-3': subgroup.courses_count }"
                  >
                    <small class="text-muted">
                      Deleted separately &mdash; stays deleted, restore from the Courses tab once this
                      {{ className.toLowerCase() }} is back:
                    </small>
                    <ul class="mb-0 mt-1">
                      <li v-for="course in subgroup.other_courses" :key="'oc-' + course.id">
                        <span class="text-capitalize">{{ course.name }}</span>
                        <span class="text-muted"> ({{ course.code }})</span>
                        <span class="text-muted"> &mdash; deleted {{ formatDate(course.deleted_at) }}</span>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </b-overlay>
      </b-tab>
    </b-tabs>

    <b-modal
      id="restoreClassModal"
      title="Restore"
      ok-title="Restore"
      ok-variant="success"
      centered
      @ok="confirmRestoreClass"
    >
      <p class="mb-3">
        Restore <span class="font-weight-bold text-capitalize">{{ pendingClass.name }}</span>?
      </p>
      <b-form-radio-group v-model="restoreWithCourses" stacked>
        <b-form-radio :value="true">
          {{ className }} and its {{ pendingClass.courses_count }}
          {{ pendingClass.courses_count === 1 ? "course" : "courses" }}
        </b-form-radio>
        <b-form-radio :value="false">
          {{ className }} only &mdash; its courses stay deleted and can be restored one by one
        </b-form-radio>
      </b-form-radio-group>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  props: {
    getCustomNames: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      fetchData: false,
      restoring: null,
      courses: [],
      classes: [],
      expanded: [],
      pendingClass: {},
      restoreWithCourses: true,
    };
  },
  computed: {
    // institutions can rename "Class" - fall back to the default when unset
    className() {
      const custom = this.getCustomNames && this.getCustomNames.userSubgroup;
      return (custom && custom.custom_name) || "Classes";
    },
  },
  created() {
    this.getDeletedResources();
  },
  methods: {
    // every deleted course of the class, both the ones its restore brings back and the ones it does not
    deletedCourseCount(subgroup) {
      const restored = subgroup.courses ? subgroup.courses.length : 0;
      const separate = subgroup.other_courses ? subgroup.other_courses.length : 0;
      return restored + separate;
    },

    toggleCourses(classId) {
      const at = this.expanded.indexOf(classId);
      if (at === -1) this.expanded.push(classId);
      else this.expanded.splice(at, 1);
    },

    async getDeletedResources() {
      this.fetchData = true;
      this.expanded = [];
      const url = this.$store.getters.getAPIKey.mainAPI + `/deletedresources`;

      await axios
        .get(url)
        .then((response) => {
          this.courses = response.data.courses;
          this.classes = response.data.classes;
        })
        .catch(() => {
          this.$toast.error("Failed to load deleted resources", {
            position: "top",
            duration: 3000,
          });
        });

      this.fetchData = false;
    },

    async restore(type, item) {
      // a class with courses needs a choice - everything, or the class on its own
      if (type === "class" && item.courses_count) {
        this.pendingClass = item;
        this.restoreWithCourses = true;
        this.$bvModal.show("restoreClassModal");
        return;
      }

      const label = type === "course" ? "course" : this.className.toLowerCase();
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Restore ${label} "${item.name}"? It will become visible again along with its data.`,
        { okTitle: "Restore", okVariant: "success", centered: true }
      );
      if (!confirmed) return;

      await this.sendRestore(type, item);
    },

    confirmRestoreClass() {
      this.sendRestore("class", this.pendingClass, this.restoreWithCourses);
    },

    async sendRestore(type, item, restoreCourses = true) {
      this.restoring = `${type}-${item.id}`;
      const base = this.$store.getters.getAPIKey.mainAPI;
      const url =
        type === "course"
          ? `${base}/courses/${item.id}/canceldeletion`
          : `${base}/usersubgroups/${item.id}/canceldeletion`;

      const payload = { reason: "Restored from deleted resources" };
      if (type === "class") payload.restore_courses = restoreCourses ? 1 : 0;

      await axios
        // the catch below shows the API's reason - skip the global toast so it isn't shown twice
        .post(url, payload, { skipGlobalError: true })
        .then(() => {
          this.$toast.success(`${item.name} restored`, {
            position: "top",
            duration: 3000,
          });
          this.getDeletedResources();
          this.$emit("restored");
        })
        .catch((error) => {
          // surface the reason from the API (e.g. the class has to be restored first)
          const reason =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : `Failed to restore ${item.name}`;
          this.$toast.error(reason, {
            position: "top",
            duration: 5000,
          });
        });

      this.restoring = null;
    },

    formatDate(value) {
      if (!value) return "";
      const date = moment.utc(value, "YYYY-MM-DD HH:mm:ss");
      return date.isValid() ? date.local().format("DD/MM/YYYY hh:mm A") : value;
    },
  },
};
</script>

<style scoped>
.deletedResources {
  padding: 5px;
}
</style>
